import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useBrowserAssist, useIssues, useLiveUpdates } from "../lib/queries.ts";
import { connection, dismissToast, openPalette, palette, toasts, useStore } from "../lib/store.ts";
import { Palette } from "./Palette.tsx";

const NAV = [
  { to: "/", label: "Agenda" },
  { to: "/inbox", label: "Inbox" },
  { to: "/history", label: "History" },
  { to: "/sources", label: "Sources" },
  { to: "/settings", label: "Settings" },
  { to: "/feedback", label: "Tell Claude" },
] as const;

export function Shell() {
  useLiveUpdates();
  useBrowserAssist();
  const issues = useIssues().data ?? [];
  const conn = useStore(connection);
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [help, setHelp] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        openPalette();
        return;
      }
      if (palette.get().open || t.closest("input, textarea, select, [contenteditable]") || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "a") { e.preventDefault(); openPalette(); }
      else if (e.key === "/") { e.preventDefault(); document.querySelector<HTMLInputElement>("#search")?.focus() ?? openPalette(); }
      else if (e.key === "?") setHelp((h) => !h);
      else if (e.key === "g") {
        const go = (ev: KeyboardEvent) => {
          const to = { a: "/", i: "/inbox", h: "/history", s: "/sources" }[ev.key];
          if (to) void navigate({ to });
        };
        window.addEventListener("keydown", go, { once: true });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const count = (to: string) => (to === "/inbox" && issues.length ? <span className="count num">{issues.length}</span> : null);

  return (
    <div className="shell">
      <aside className="rail">
        <Link to="/" className="wordmark">Hammer</Link>
        <nav className="grid gap-0.5" aria-label="Main">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} activeOptions={{ exact: n.to === "/" }}>
              {n.label}
              {count(n.to)}
            </Link>
          ))}
        </nav>
        <button className="btn btn-primary w-full" onClick={() => openPalette()}>Add a lot</button>
        <div className="mt-auto text-[12.5px] faint grid gap-1">
          <span>{conn === "live" ? "Live updates on" : conn === "offline" ? "Offline: showing saved data" : "Reconnecting…"}</span>
          <button className="text-left underline underline-offset-2 bg-transparent border-0 p-0 faint" onClick={() => setHelp(true)}>Keyboard shortcuts</button>
        </div>
      </aside>

      <header className="topbar">
        <Link to="/" className="wordmark">Hammer</Link>
        <div className="flex items-center gap-2">
          {conn !== "live" && <span className="text-[12px] faint">{conn === "offline" ? "Offline" : "Reconnecting"}</span>}
          <button className="btn btn-primary" onClick={() => openPalette()}>Add</button>
        </div>
      </header>

      <main className="main" id="main">
        <Outlet />
      </main>

      <nav className="tabbar" aria-label="Main">
        {NAV.filter((n) => n.to !== "/sources" && n.to !== "/feedback").map((n) => (
          <Link key={n.to} to={n.to} activeOptions={{ exact: n.to === "/" }}>
            {n.to === "/settings" ? "More" : n.label}
            {count(n.to)}
          </Link>
        ))}
      </nav>

      <Palette />
      <Toasts />
      {help && <ShortcutHelp onClose={() => setHelp(false)} path={path} />}
    </div>
  );
}

function Toasts() {
  const list = useStore(toasts);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className="toast" data-tone={t.tone}>
          <span className="min-w-0 truncate">{t.text}</span>
          {t.action && <button onClick={() => { t.action!.run(); dismissToast(t.id); }}>{t.action.label}</button>}
        </div>
      ))}
    </div>
  );
}

const SHORTCUTS: [string, string][] = [
  ["a or ⌘K", "Add a lot, or jump to one"],
  ["/", "Search the agenda"],
  ["j / k", "Move down / up the list"],
  ["Enter", "Open the selected lot"],
  ["w / b / p", "Watch / Bid / Pass the selected lot"],
  ["e", "Archive the selected lot"],
  ["o", "Open the lot on its auction site"],
  ["g then a, i, h, s", "Go to Agenda, Inbox, History, Sources"],
  ["?", "Show or hide this list"],
];

function ShortcutHelp({ onClose }: { onClose: () => void; path: string }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog p-5" role="dialog" aria-label="Keyboard shortcuts">
        <h2 className="heading text-[21.6px] mb-3">Keyboard shortcuts</h2>
        <dl className="kv">
          {SHORTCUTS.map(([k, d]) => (
            <div key={k} className="contents"><dt className="num">{k}</dt><dd>{d}</dd></div>
          ))}
        </dl>
      </div>
    </div>
  );
}
